import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './GlobalSearch.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const GlobalSearch = ({ onNavigate, onStartConversation }) => {
  const { authFetch, user: currentUser } = useAuth();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [followingStatus, setFollowingStatus] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const [results, setResults] = useState({
    users: [],
    clubs: [],
    groups: [],
    races: []
  });
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown and collapse when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
        if (query.length === 0) {
          setIsExpanded(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [query]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
        setIsExpanded(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  const handleIconClick = () => {
    setIsExpanded(true);
  };

  const performSearch = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setResults({ users: [], clubs: [], groups: [], races: [] });
      setFollowingStatus({});
      return;
    }

    setLoading(true);

    try {
      const [usersRes, clubsRes, racesRes] = await Promise.all([
        authFetch(`/api/users/search?q=${encodeURIComponent(searchQuery)}&limit=5`),
        authFetch(`/api/clubs/search?q=${encodeURIComponent(searchQuery)}&limit=5`),
        authFetch(`/api/races/library?query=${encodeURIComponent(searchQuery)}&limit=5`)
      ]);

      const usersData = usersRes.ok ? await usersRes.json() : { users: [] };
      const clubsData = clubsRes.ok ? await clubsRes.json() : { clubs: [] };
      const racesData = racesRes.ok ? await racesRes.json() : { races: [] };

      // Separate clubs and groups
      const allClubs = clubsData.clubs || [];
      const clubs = allClubs.filter(c => c.club_type === 'club');
      const groups = allClubs.filter(c => c.club_type === 'group');

      const users = usersData.users || [];

      // Build follow status from user data (if available) or check each user
      const statusMap = {};
      users.forEach(user => {
        // The search API might return is_following field
        statusMap[user.id] = user.is_following || false;
      });
      setFollowingStatus(statusMap);

      setResults({
        users,
        clubs,
        groups,
        races: racesData.races || []
      });
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(value.length >= 2);

    // Debounce the search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  const handleFollow = async (e, userId) => {
    e.stopPropagation();
    if (actionLoading[userId]) return;

    setActionLoading(prev => ({ ...prev, [userId]: true }));

    try {
      const isCurrentlyFollowing = followingStatus[userId];
      const method = isCurrentlyFollowing ? 'DELETE' : 'POST';

      const response = await authFetch(`/api/follows/${userId}`, { method });

      if (response.ok) {
        setFollowingStatus(prev => ({
          ...prev,
          [userId]: !isCurrentlyFollowing
        }));
      }
    } catch (err) {
      console.error('Follow action failed:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleMessage = (e, user) => {
    e.stopPropagation();
    setIsOpen(false);
    setQuery('');
    setIsExpanded(false);

    if (onStartConversation) {
      onStartConversation(user);
    } else {
      // Fallback: navigate to messages
      onNavigate('messages');
    }
  };

  const handleResultClick = (type, item) => {
    setIsOpen(false);
    setQuery('');

    switch (type) {
      case 'club':
      case 'group':
        onNavigate('clubs');
        break;
      case 'race':
        onNavigate('races');
        break;
      default:
        break;
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  const hasResults = results.users.length > 0 || results.clubs.length > 0 ||
                     results.groups.length > 0 || results.races.length > 0;

  return (
    <div className={`global-search ${isExpanded ? 'global-search--expanded' : ''}`} ref={searchRef}>
      {!isExpanded ? (
        <button className="global-search__trigger" onClick={handleIconClick} title="Search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
        </button>
      ) : (
        <div className="global-search__input-wrapper">
          <svg className="global-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            className="global-search__input"
            placeholder="Search users, clubs, races..."
            value={query}
            onChange={handleInputChange}
            onFocus={() => query.length >= 2 && setIsOpen(true)}
          />
          {loading && <div className="global-search__spinner" />}
        </div>
      )}

      {isOpen && (
        <div className="global-search__dropdown">
          {!hasResults && !loading && query.length >= 2 && (
            <div className="global-search__no-results">
              No results found for "{query}"
            </div>
          )}

          {results.users.length > 0 && (
            <div className="global-search__section">
              <div className="global-search__section-header">Users</div>
              {results.users.map((user) => {
                let profileImageUrl = user.profile_image;
                if (profileImageUrl && !profileImageUrl.startsWith('http')) {
                  profileImageUrl = `${API_BASE_URL}${profileImageUrl}`;
                }
                const displayName = user.name || user.email?.split('@')[0] || 'User';
                const isFollowing = followingStatus[user.id];
                const isLoading = actionLoading[user.id];
                const isCurrentUser = currentUser && currentUser.id === user.id;

                return (
                  <div key={`user-${user.id}`} className="global-search__user-result">
                    <div className="global-search__user-info">
                      <div className="global-search__result-avatar">
                        {profileImageUrl ? (
                          <img src={profileImageUrl} alt={displayName} />
                        ) : (
                          <span>{getInitials(displayName)}</span>
                        )}
                      </div>
                      <div className="global-search__result-info">
                        <span className="global-search__result-name">{displayName}</span>
                        <span className="global-search__result-meta">{user.email}</span>
                      </div>
                    </div>
                    {!isCurrentUser && (
                      <div className="global-search__user-actions">
                        <button
                          className={`global-search__action-btn ${isFollowing ? 'following' : 'follow'}`}
                          onClick={(e) => handleFollow(e, user.id)}
                          disabled={isLoading}
                        >
                          {isLoading ? '...' : isFollowing ? 'Following' : 'Follow'}
                        </button>
                        <button
                          className="global-search__action-btn message"
                          onClick={(e) => handleMessage(e, user)}
                          title="Message"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 3C6.48 3 2 6.58 2 11c0 2.13 1.02 4.07 2.68 5.49-.17.84-.68 2.1-2.15 3.38 0 0 3.24-.27 5.56-1.87.61.12 1.26.19 1.91.19 5.52 0 10-3.58 10-8S17.52 3 12 3z" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {results.clubs.length > 0 && (
            <div className="global-search__section">
              <div className="global-search__section-header">Clubs</div>
              {results.clubs.map((club) => (
                <button
                  key={`club-${club.id}`}
                  className="global-search__result"
                  onClick={() => handleResultClick('club', club)}
                >
                  <div className="global-search__result-icon club">
                    <span>C</span>
                  </div>
                  <div className="global-search__result-info">
                    <span className="global-search__result-name">{club.name}</span>
                    <span className="global-search__result-meta">
                      {club.member_count} members
                      {club.is_private ? ' • Private' : ''}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {results.groups.length > 0 && (
            <div className="global-search__section">
              <div className="global-search__section-header">Groups</div>
              {results.groups.map((group) => (
                <button
                  key={`group-${group.id}`}
                  className="global-search__result"
                  onClick={() => handleResultClick('group', group)}
                >
                  <div className="global-search__result-icon group">
                    <span>G</span>
                  </div>
                  <div className="global-search__result-info">
                    <span className="global-search__result-name">{group.name}</span>
                    <span className="global-search__result-meta">
                      {group.member_count} members
                      {group.is_private ? ' • Private' : ''}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {results.races.length > 0 && (
            <div className="global-search__section">
              <div className="global-search__section-header">Races</div>
              {results.races.map((race) => (
                <button
                  key={`race-${race.id}`}
                  className="global-search__result"
                  onClick={() => handleResultClick('race', race)}
                >
                  <div className="global-search__result-icon race">
                    <span>R</span>
                  </div>
                  <div className="global-search__result-info">
                    <span className="global-search__result-name">{race.name}</span>
                    <span className="global-search__result-meta">
                      {race.distance} • {race.location}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
